import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Slider } from "./ui/slider";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { toast } from "sonner";
import { Check, Loader2, Sparkles, Zap, ShoppingCart } from "lucide-react";

// Using relative URL for API calls
const API = "/api";

const PLANS = [
  {
    id: "free",
    name: "Free",
    price: 0,
    description: "For individuals getting started",
    features: [
      "3 conversations per month",
      "Basic ZOEY assistance",
      "Email support",
    ],
    icon: Sparkles,
    current: true,
  },
  {
    id: "pro",
    name: "Pro",
    price: 29.00,
    description: "For growing businesses",
    features: [
      "Unlimited conversations",
      "Priority ZOEY assistance",
      "Advanced analytics",
      "Priority support",
      "Export conversations",
    ],
    icon: Zap,
    popular: true,
  },
];

export default function UpgradePlanModal({ open, onOpenChange, user, onUserUpdate }) {
  const [loading, setLoading] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [plusConversations, setPlusConversations] = useState(10); // Minimum 10 conversations

  // Check for payment success on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get("session_id");
    
    if (sessionId && open) {
      const checkStatus = async (sid) => {
        setCheckingPayment(true);
        const maxAttempts = 5;
        const pollInterval = 2000;

        const poll = async (attempts = 0) => {
          if (attempts >= maxAttempts) {
            toast.error("Payment verification timed out. Please contact support.");
            setCheckingPayment(false);
            return;
          }

          try {
            const token = localStorage.getItem("auth_token");
            const response = await fetch(`${API}/payments/status/${sid}`, {
              headers: { Authorization: `Bearer ${token}` },
              
            });

            if (!response.ok) {
              throw new Error("Failed to check payment status");
            }

            const data = await response.json();

            if (data.payment_status === "paid") {
              toast.success("Payment successful! Your conversations have been added.");
              // Refresh user data
              const userResponse = await fetch(`${API}/auth/me`, {
                headers: { Authorization: `Bearer ${token}` },
                
              });
              if (userResponse.ok) {
                const updatedUser = await userResponse.json();
                onUserUpdate(updatedUser);
                localStorage.setItem("user", JSON.stringify(updatedUser));
              }
              setCheckingPayment(false);
              onOpenChange(false);
              return;
            } else if (data.status === "expired") {
              toast.error("Payment session expired. Please try again.");
              setCheckingPayment(false);
              return;
            }

            // Continue polling
            setTimeout(() => poll(attempts + 1), pollInterval);
          } catch (error) {
            console.error("Error checking payment status:", error);
            if (attempts < maxAttempts - 1) {
              setTimeout(() => poll(attempts + 1), pollInterval);
            } else {
              toast.error("Error verifying payment. Please contact support.");
              setCheckingPayment(false);
            }
          }
        };

        poll();
      };
      
      checkStatus(sessionId);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [open, onOpenChange, onUserUpdate]);

  const handleUpgrade = async (planId, extraData = {}) => {
    if (planId === "free") return;

    setLoading(planId);
    try {
      const token = localStorage.getItem("auth_token");
      const originUrl = window.location.origin;

      const response = await fetch(`${API}/payments/checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          plan_id: planId,
          origin_url: originUrl,
          ...extraData,
        }),
        
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Failed to create checkout session");
      }

      const data = await response.json();
      
      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (error) {
      toast.error(error.message);
      setLoading(null);
    }
  };

  const handlePlusPurchase = () => {
    handleUpgrade("plus", { conversations: plusConversations });
  };

  const currentPlan = user?.plan || "free";
  const plusPrice = plusConversations; // $1 per conversation

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1E1E1E] border-[#333] text-white max-w-3xl" data-testid="upgrade-plan-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-center">
            Upgrade Your Plan
          </DialogTitle>
          <p className="text-center text-[#888] text-sm mt-2">
            Choose the plan that best fits your business needs
          </p>
        </DialogHeader>

        {checkingPayment && (
          <div className="flex flex-col items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-[#14A800] mb-4" />
            <p className="text-[#888]">Verifying your payment...</p>
          </div>
        )}

        {!checkingPayment && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            {/* Free and Pro Plans */}
            {PLANS.map((plan) => {
              const Icon = plan.icon;
              const isCurrentPlan = currentPlan.toLowerCase() === plan.id.toLowerCase() || 
                (currentPlan === "Free Plan" && plan.id === "free");
              
              return (
                <div
                  key={plan.id}
                  className={`relative p-5 rounded-xl border transition-all ${
                    plan.popular
                      ? "border-[#14A800] bg-[#14A800]/5"
                      : "border-[#333] bg-[#0D0D0D]"
                  } ${isCurrentPlan ? "ring-2 ring-[#14A800]/50" : ""}`}
                  data-testid={`plan-card-${plan.id}`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-[#14A800] text-white text-xs px-3 py-1 rounded-full font-medium">
                        Most Popular
                      </span>
                    </div>
                  )}

                  <div className="flex items-center gap-2 mb-3">
                    <div className={`p-2 rounded-lg ${plan.popular ? "bg-[#14A800]/20" : "bg-[#333]"}`}>
                      <Icon className={`w-5 h-5 ${plan.popular ? "text-[#14A800]" : "text-[#888]"}`} />
                    </div>
                    <h3 className="font-semibold text-lg">{plan.name}</h3>
                  </div>

                  <div className="mb-4">
                    <span className="text-3xl font-bold">${plan.price}</span>
                    {plan.price > 0 && <span className="text-[#888] text-sm">/month</span>}
                  </div>

                  <p className="text-sm text-[#888] mb-4">{plan.description}</p>

                  <ul className="space-y-2 mb-6">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-[#14A800] flex-shrink-0" />
                        <span className="text-[#E8E8E8]">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    onClick={() => handleUpgrade(plan.id)}
                    disabled={isCurrentPlan || loading === plan.id}
                    className={`w-full ${
                      plan.popular
                        ? "bg-[#14A800] hover:bg-[#108A00] text-white"
                        : "bg-[#2A2A2A] hover:bg-[#333] text-white border border-[#444]"
                    } ${isCurrentPlan ? "opacity-50 cursor-not-allowed" : ""}`}
                    data-testid={`plan-select-${plan.id}`}
                  >
                    {loading === plan.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : isCurrentPlan ? (
                      "Current Plan"
                    ) : (
                      `Upgrade to ${plan.name}`
                    )}
                  </Button>
                </div>
              );
            })}

            {/* Plus Plan - A La Carte */}
            <div
              className="relative p-5 rounded-xl border border-[#333] bg-[#0D0D0D]"
              data-testid="plan-card-plus"
            >
              <div className="flex items-center gap-2 mb-3">
                <div className="p-2 rounded-lg bg-[#333]">
                  <ShoppingCart className="w-5 h-5 text-[#888]" />
                </div>
                <h3 className="font-semibold text-lg">Plus</h3>
              </div>

              <div className="mb-4">
                <span className="text-3xl font-bold">${plusPrice}</span>
                <span className="text-[#888] text-sm"> one-time</span>
              </div>

              <p className="text-sm text-[#888] mb-4">Buy conversations à la carte</p>

              {/* Slider for selecting conversations */}
              <div className="mb-4">
                <div className="flex justify-between text-sm text-[#888] mb-2">
                  <span>Conversations</span>
                  <span className="text-[#14A800] font-medium">{plusConversations}</span>
                </div>
                <Slider
                  value={[plusConversations]}
                  onValueChange={(value) => setPlusConversations(value[0])}
                  min={10}
                  max={50}
                  step={5}
                  className="w-full"
                  data-testid="plus-slider"
                />
                <div className="flex justify-between text-xs text-[#666] mt-1">
                  <span>$10 min</span>
                  <span>$50 max</span>
                </div>
              </div>

              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-[#14A800] flex-shrink-0" />
                  <span className="text-[#E8E8E8]">{plusConversations} extra conversations</span>
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-[#14A800] flex-shrink-0" />
                  <span className="text-[#E8E8E8]">$1 per conversation</span>
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-[#14A800] flex-shrink-0" />
                  <span className="text-[#E8E8E8]">Never expires</span>
                </li>
              </ul>

              <Button
                onClick={handlePlusPurchase}
                disabled={loading === "plus"}
                className="w-full bg-[#2A2A2A] hover:bg-[#333] text-white border border-[#444]"
                data-testid="plan-select-plus"
              >
                {loading === "plus" ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  `Buy ${plusConversations} for $${plusPrice}`
                )}
              </Button>
            </div>
          </div>
        )}

        <p className="text-center text-xs text-[#666] mt-4">
          Secure payment powered by Stripe. Cancel anytime.
        </p>
      </DialogContent>
    </Dialog>
  );
}
